import { z } from 'zod';
import { DecisionStatus } from './decision-status.js';
/**
 * Decisions register (`/decisions`). One Firestore doc per recorded
 * decision — sourced from agent-audited approvals, Slack threads, and
 * meeting notes that explicitly captured a call. Append-only-ish:
 * title/body/topic are pinned at creation; status/confidence/revisit/
 * reversal evolve through `PATCH`. Every mutation stamps an
 * `audit/decision.*` event.
 *
 * Multi-tenant: every doc carries `orgId`; reads gate via
 * `isOrgMember(orgId)`, writes require ownership (`decidedByUid ===
 * actorUid`) OR `orgAdmin`. Hard-deletes are admin-only — the log is the
 * historical record, REVERSED + SUPERSEDED stay visible after-the-fact.
 *
 * Schema invariants:
 *   - `decidedByLabel` is denormalized at write time so the card can
 *     render the decider through `<EntityName>` without a per-row
 *     `users/:uid` lookup. Update via the patch endpoint isn't allowed
 *     today — it would re-fetch on next render.
 *   - `revisitAt` is meaningful when `status === 'REVISIT'`; the
 *     right-rail "upcoming revisits" query orders by it. Set to `null`
 *     in a PATCH to clear.
 *   - `reversedAt` + `reversedReason` are required together when
 *     transitioning to `REVERSED` (enforced at the router boundary, not
 *     in this schema, so partial PATCHes stay flexible).
 */
export const DECISIONS_COLLECTION = 'decisions';
/**
 * Coarse "how sure was the operator at decision time" bucket. Persisted
 * verbatim so the filter rail can `where('confidence','==', ...)` without
 * a numeric bucketer; the card's `<ConfidenceMeter>` widget renders the
 * pct-style label via STRINGS lookup.
 */
export const DecisionConfidence = z.enum(['high', 'medium', 'low']);
/** One bullet of rationale. The card renders up to 3 in the callout
 *  ("Rationale" block on `--color-surface-2`); the server doesn't cap. */
export const DecisionRationale = z.object({
    text: z.string().min(1).max(280),
});
/**
 * Closed string set of evidence types a decision can cite. `audit-event`
 * / `dossier` / `slack-thread` / `meeting-note` carry typed ids;
 * `external-url` carries a URL.
 */
export const DecisionSourceKind = z.enum([
    'audit-event',
    'dossier',
    'slack-thread',
    'meeting-note',
    'external-url',
]);
/**
 * A source the decision was drawn from. `kind` is the closed string set
 * of evidence types the agent can cite today; `ref` is the typed id for
 * `audit-event` / `dossier` / `slack-thread` / `meeting-note` and a
 * URL for `external-url`. `label` is the denormalized display text.
 */
export const DecisionSource = z.object({
    kind: DecisionSourceKind,
    ref: z.string().min(1),
    label: z.string().min(1).max(120),
});
export const Decision = z.object({
    id: z.string(),
    orgId: z.string(),
    title: z.string().min(1).max(160),
    body: z.string().max(2000).optional(),
    /** Free-form, normalized lowercase server-side. Drives the right-rail
     *  "by topic" donut + the filter-rail topic dropdown. */
    topic: z.string().min(1).max(60),
    status: DecisionStatus,
    confidence: DecisionConfidence,
    decidedByUid: z.string(),
    /** Denormalized at write time — see header. */
    decidedByLabel: z.string(),
    decidedAt: z.string(),
    revisitAt: z.string().optional(),
    reversedAt: z.string().optional(),
    reversedReason: z.string().max(280).optional(),
    rationale: z.array(DecisionRationale).max(8).default([]),
    sources: z.array(DecisionSource).max(20).default([]),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * Create-request body. Server stamps `id`, `decidedByUid`,
 * `decidedByLabel`, `createdAt`, `updatedAt`; `decidedAt` defaults to
 * the server clock when omitted so a quick "log this call now" capture
 * doesn't need a client-side timestamp.
 */
export const CreateDecisionRequest = Decision.pick({
    title: true,
    body: true,
    topic: true,
    status: true,
    confidence: true,
    revisitAt: true,
    rationale: true,
    sources: true,
}).extend({
    decidedAt: z.string().optional(),
});
/**
 * Patch body. `revisitAt: null` clears the field (the
 * `FieldValue.delete()` route is server-internal). `rationaleAppend`
 * appends bullets to the existing array — the server enforces the
 * 8-row cap from {@link Decision} on the merged result.
 *
 * Immutable fields (title / body / topic / decidedByUid /
 * decidedByLabel / decidedAt / createdAt) are deliberately absent so a
 * malformed PATCH can't sneak a rewrite past the audit trail.
 */
export const UpdateDecisionRequest = z.object({
    status: DecisionStatus.optional(),
    confidence: DecisionConfidence.optional(),
    revisitAt: z.string().nullable().optional(),
    reversedAt: z.string().optional(),
    reversedReason: z.string().max(280).optional(),
    rationaleAppend: z.array(DecisionRationale).max(4).optional(),
});
/** List response — newest decisions first, ordered server-side. */
export const ListDecisionsResponse = z.object({
    decisions: z.array(Decision),
});
